import { createContext, useContext } from 'react'
import type { Bundle } from '../lib/transfer'
import type { Reisestart } from '../lib/trip'
import type {
  Checks,
  DiaryEntry,
  DiaryPost,
  Expense,
  PhotoMeta,
  Stage,
  Trip,
  TripMeta,
  Vehicle,
} from '../lib/types'
import type { SyncStatus } from './sync'

/** Alles, was die Oberfläche vom Datenbestand braucht. */
export interface RoadbookValue {
  /** Falsch, solange der lokale Bestand noch geladen wird. */
  ready: boolean
  /**
   * Die Reise, zu der dieser Bestand gehört.
   *
   * Leer heisst: der Bestand ist noch keiner Reise zugeordnet (siehe ALTBESTAND
   * in data/db.ts). Dann läuft die App, aber es wird nicht abgeglichen.
   */
  tripId: string
  /**
   * Ob in dieser Reise geschrieben werden darf - Rolle modify oder admin.
   *
   * Die Oberfläche zeigt bei false keine Schreibfelder und keine Knöpfe an.
   * Das ist keine Sicherheitsmassnahme, die steht auf dem Server; es ist die
   * Zusage, dass niemand etwas eintippt, was gleich darauf verworfen wird.
   */
  darfSchreiben: boolean
  trip: Trip
  /**
   * Die Tatsachen des Tages je Etappen-ID: Haken, Datum, Tacho, Wetter, Track.
   * Was jemand geschrieben hat, steht in postsByStage.
   */
  diary: Record<string, DiaryEntry>
  /**
   * Die Tagebuchbeiträge je Etappen-ID, ältester zuerst.
   *
   * Enthält auch den Text aus der Zeit vor den Beiträgen, sobald es ihn noch
   * gibt: er wird beim Lesen zu einem Beitrag, damit nichts unsichtbar wird,
   * bevor die Überführung ihn festgeschrieben hat. Siehe lib/diary.ts.
   */
  postsByStage: Record<string, DiaryPost[]>
  expenses: Expense[]
  checks: Checks
  /** Fotos je Etappen-ID, älteste zuerst. */
  photosByStage: Record<string, PhotoMeta[]>

  sync: SyncStatus
  /**
   * Stösst einen Abgleich an und kehrt zurück, wenn er durch ist.
   *
   * Das Versprechen ist die Zusage, auf die sich die Oberfläche verlassen
   * darf: sie muss den Abschluss nicht am Statusobjekt ablesen, also nicht
   * wiederholt nachsehen, ob wieder 'syncing' vorbei ist. Ein Aufruf ohne await
   * bleibt ausdrücklich erlaubt und verhält sich wie bisher.
   *
   * Erfüllt wird es in jedem Fall, auch wenn der Abgleich scheitert oder gar
   * keiner laufen kann. Fehler stehen im Statusobjekt, nicht in der Ablehnung.
   */
  syncNow: () => Promise<void>

  /**
   * Löscht den gesamten lokalen Bestand für das Abmelden.
   *
   * Der einzige zulässige Weg dorthin: wipe() aus data/db.ts gehört nicht mehr
   * von ausserhalb der Datenschicht gerufen. Es leert die Datenbank unter dem
   * laufenden Abgleich weg, der daraufhin den Cursor auf 0 liest und den ganzen
   * Bestand des eben abgemeldeten Nutzers vom Server zurückholt. Diese Funktion
   * legt den Abgleich erst still, wartet den laufenden Durchgang ab und löscht
   * dann - und verwirft dabei auch Schreibvorgänge, die noch unterwegs sind.
   *
   * Kehrt zurück, wenn nichts mehr auf dem Gerät liegt. Die Anmeldeschicht
   * muss darauf warten, bevor sie den Anmeldedienst anspricht.
   */
  wipeForSignOut: () => Promise<void>

  setMeta: (patch: Partial<TripMeta>) => void
  /**
   * Ändert das Fahrzeug der Reise.
   *
   * Der einzige Weg dorthin: das Fahrzeug steht im Reisedokument, und Name und
   * Verbrauch gehen zusätzlich in die alten Felder der Rahmendaten, damit eine
   * ältere Fassung der App sie weiter findet. Wer stattdessen setMeta benutzt,
   * schriebe nur die Kopie und nicht das Fahrzeug - siehe mitFahrzeug in
   * lib/vehicle.ts.
   */
  setVehicle: (patch: Partial<Vehicle>) => void
  setStage: (id: string, patch: Partial<Stage>) => void
  /**
   * Legt hinter afterId eine Etappe an und gibt deren Kennung zurück.
   *
   * Der Start kommt vom Ziel der vorherigen Etappe. Gibt es keine - die erste
   * Etappe einer leeren Reise -, muss er mitgegeben werden: eine Etappe, die
   * irgendwo anfängt, entsteht hier nicht. Ohne beides bleibt die Reise, wie
   * sie war, und zurück kommt eine leere Kennung.
   */
  addStage: (afterId: string, start?: Reisestart) => string
  /**
   * Hängt fertige Etappen ans Ende der Reise.
   *
   * Für die Kette, die aus einer Aufzeichnung entsteht: sie kommt als Ganzes
   * herein, damit sie in einem Schreibvorgang entsteht und nicht aus lauter
   * Zwischenständen. Bestehende Etappen werden dabei nicht angefasst -
   * angehängt wird hinten, eingefügt wird nirgends.
   */
  appendStages: (stages: Stage[]) => void
  deleteStage: (id: string) => void
  shiftStage: (id: string, direction: -1 | 1) => void

  setDiary: (stageId: string, patch: Partial<DiaryEntry>) => void

  /**
   * Legt einen neuen Beitrag an. Leerer Text tut nichts.
   *
   * Ein neuer Beitrag und kein Verlängern des vorigen: genau daran hängt, dass
   * zwei Leute am selben Abend nicht in dasselbe Dokument schreiben.
   */
  addPost: (stageId: string, text: string) => void
  /**
   * Ändert den Text eines Beitrags. Leerer Text löscht ihn.
   *
   * Nur der Verfasser darf das. Die Prüfung steht in der Datenschicht und in
   * der Oberfläche - aber noch nicht auf dem Server, siehe darfBearbeiten in
   * lib/diary.ts.
   */
  updatePost: (id: string, text: string) => void
  /** Löscht einen Beitrag. Nur der Verfasser darf das, siehe updatePost. */
  deletePost: (id: string) => void

  addExpense: (expense: Omit<Expense, 'id'>) => void
  deleteExpense: (id: string) => void

  /**
   * Ersetzt eine oder beide Checklisten.
   *
   * Übergeben wird die fertige Liste, nicht ein einzelner Handgriff darauf: das
   * Abhaken, Ändern, Umsortieren und Entfernen läuft in der Oberfläche über
   * dieselben reinen Listenoperationen wie bei den Etappeninhalten
   * (features/stages/stageLists.ts), und die geben eine neue Liste zurück. Ein
   * eigener Aufruf je Handgriff wäre dieselbe Logik ein zweites Mal, hier ohne
   * Test.
   */
  setChecks: (patch: Partial<Checks>) => void

  addPhoto: (stageId: string, file: Blob) => Promise<void>
  deletePhoto: (id: string) => void
  /** Bilddaten eines Fotos, lokal oder vom Server. */
  loadPhoto: (id: string, stageId: string) => Promise<Blob | null>

  /**
   * Baut die Exportdatei mit allen Daten und Bildern.
   *
   * @param onFortschritt wird während des Packens je Foto gerufen, mit der Zahl
   *   der fertigen und der gesamten. Bei hundert Bildern dauert der Export
   *   sichtbar lange; ohne Anzeige sieht das aus wie eine hängende App.
   */
  buildExport: (onFortschritt?: (fertig: number, gesamt: number) => void) => Promise<Blob>
  /**
   * Ersetzt den gesamten Bestand durch den Inhalt einer Exportdatei.
   *
   * Verbraucht das Bundle: die Bilddaten werden freigegeben, sobald sie in der
   * Datenbank liegen, sonst hält eine grosse Datei jedes Foto gleichzeitig als
   * Text und als Bytes im Speicher. Ein zweiter Aufruf mit demselben Bundle
   * schriebe die Fotos ohne Bild.
   */
  importBundle: (bundle: Bundle) => Promise<void>
}

export const RoadbookContext = createContext<RoadbookValue | null>(null)

export function useRoadbook(): RoadbookValue {
  const value = useContext(RoadbookContext)
  if (!value) throw new Error('useRoadbook ausserhalb von RoadbookProvider benutzt')
  return value
}
